import { useMemo } from 'react';
import { DataTable } from './UsermanagerTable';
import { ColumnDef } from '@tanstack/react-table';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDateToGerman } from '@/lib/formatDate';
import { Badge } from '@/components/ui/badge';
import Footer from './Footer';
import Box from './StyledHtmlElements/Box';
import CreateUserForm from './CreateUserForm';
import { extracEmailFromUser, getGlobalAdminName } from '@/utils/helpers';
import { useToast } from '@/hooks/use-toast';
import { useCognito } from '@/hooks/use-cognito';
import { useUser } from '@/hooks/user-user';
import UsermanagmentDropdown from './UsermanagmentDropdown';

export type User = {
  id: string;
  email: string;
  groups: string[];
  creationDate: Date | undefined;
  lastModifiedDate: Date | undefined;
  status: string;
  isGroupAdmin: boolean;
  isGlobalAdmin: boolean;
  hasScoutAccess: boolean;
  hasClubAccess: boolean;
};

const GroupManagerWindow = () => {
  const { isGlobalAdmin, teamName, isGroupAdmin } = useUser();
  const { toast, toasts } = useToast();
  const {
    userGroupsMapping,
    userList,

    loading,
  } = useCognito({
    errorHandler: displayErrorToast,
  });

  function displayErrorToast(err: string) {
    if (toasts.length > 0) return;

    toast({
      title: 'Fehler',
      description: err,
      variant: 'destructive',
      duration: 5000,
    });
  }

  // build the user data from the userGroupsMapping for usage in the table
  const generateUserList = (): User[] => {
    //ensure that the userGroupsMapping is not null and that the user has a username and groups
    const nonNullableUserGroupsMapping = userGroupsMapping.filter((user) => {
      return user.username && user.Groups;
    });

    // only for groupd admins filter the user list to only show the users belong to the group the user is in
    const availableUserList = isGlobalAdmin
      ? nonNullableUserGroupsMapping
      : nonNullableUserGroupsMapping.filter((user) => {
          const allUserGroups =
            (user.Groups?.map((group) => group.GroupName).filter((groupName) => groupName) as string[]) || [];
          return allUserGroups.some((groupName) => groupName.toLowerCase().includes(teamName.toLowerCase()));
        });

    return availableUserList.map((user) => {
      const allUserGroups =
        (user.Groups?.map((group) => group.GroupName).filter((groupName) => groupName) as string[]) || [];
      const userDetail = userList!.find((u) => extracEmailFromUser(u) === user.username);
      const userTeamName = allUserGroups.find((groupName) => !groupName.toLowerCase().includes('admin')) || '';
      const isGroupAdmin = allUserGroups
        .filter((groupName) => groupName.toLowerCase().includes(userTeamName))
        .some((groupName) => groupName.toLowerCase().includes('groupadmins'));
      const isGlobalAdmin = allUserGroups.includes(getGlobalAdminName());
      const hasScoutAccess =
        userDetail?.Attributes?.find((attr) => attr.Name === 'custom:scout_access')?.Value === 'true' || false;
      const hasClubAccess =
        userDetail?.Attributes?.find((attr) => attr.Name === 'custom:club_access')?.Value === 'true' || false;

      return {
        id: user.username,
        email: user.username,
        groups: allUserGroups,
        creationDate: userDetail?.UserCreateDate,
        lastModifiedDate: userDetail?.UserLastModifiedDate,
        status: userDetail?.UserStatus || '',
        isGroupAdmin,
        isGlobalAdmin,
        hasScoutAccess,
        hasClubAccess,
      };
    });
  };

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'email',
      header: 'E-Mail',
      enableSorting: true,
    },

    {
      accessorKey: 'groups',
      header: 'Verein',
      cell: ({ row }) => {
        const user = row.original;
        const noAdminGroups = user.groups.filter((group) => !group.toLowerCase().includes('groupadmins'));

        return (
          <p
            className="min-w-[100px] max-w-[200px] sm:min-w-[100px] sm:max-w-[150px] truncate whitespace-nowrap overflow-hidden capitalize"
            title={noAdminGroups.join(', ')}>
            {noAdminGroups.join(', ')}
          </p>
        );
      },
    },

    {
      accessorKey: 'hasScoutAccess',
      header: 'Scouting Zugriff',
      cell: ({ row }) => {
        return row.original.hasScoutAccess ? <Check className="text-green-500" /> : null;
      },
    },

    {
      accessorKey: 'hasClubAccess',
      header: 'Club Controlling Zugriff',
      cell: ({ row }) => {
        return row.original.hasClubAccess ? <Check className="text-green-500" /> : null;
      },
    },

    {
      accessorKey: 'isGroupAdmin',
      header: 'Gruppen Admin',
      cell: ({ row }) => {
        return row.original.isGroupAdmin ? <Check className="text-green-500" /> : null;
      },
    },

    {
      accessorKey: 'isGlobalAdmin',
      header: 'Icehawk Admin',
      cell: ({ row }) => {
        return row.original.isGlobalAdmin ? <Check className="text-green-500" /> : null;
      },
    },

    //user creation date
    {
      accessorKey: 'creationDate',
      header: 'Erstellt am',
      cell: ({ row }) => {
        const creationDate = row.original.creationDate;
        return <p>{creationDate ? formatDateToGerman(creationDate) : ''}</p>;
      },
      enableSorting: true,
      sortingFn: (a, b) => {
        const dateA = a.original.creationDate?.getTime() || 0;
        const dateB = b.original.creationDate?.getTime() || 0;
        return dateA - dateB;
      },
    },

    // user last modified date
    {
      accessorKey: 'lastModifiedDate',
      header: 'Zuletzt bearbeitet',
      cell: ({ row }) => {
        const lastModifiedDate = row.original.lastModifiedDate;
        return <p>{lastModifiedDate ? formatDateToGerman(lastModifiedDate) : ''}</p>;
      },
      enableSorting: true,
      sortingFn: (a, b) => {
        const dateA = a.original.creationDate?.getTime() || 0;
        const dateB = b.original.creationDate?.getTime() || 0;
        return dateA - dateB;
      },
    },

    //user status
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: true,
      cell: ({ row }) => {
        const userStatus = row.original.status;
        let badgeText = '';
        let badgeColor = '';

        switch (userStatus) {
          case 'CONFIRMED':
            badgeText = 'Bestätigt';
            badgeColor = 'bg-green-200 dark:bg-green-600/60';
            break;
          case 'FORCE_CHANGE_PASSWORD':
            badgeText = 'Passwort ändern';
            badgeColor = 'bg-orange-200 dark:bg-orange-600/60';
            break;
          case 'RESET_REQUIRED':
            badgeText = 'Passwort zurücksetzen';
            badgeColor = 'bg-red-200 dark:bg-red-700/60';
            break;
          case 'UNCONFIRMED':
            badgeText = 'Nicht bestätigt';
            badgeColor = 'bg-red-200 dark:bg-red-700';
            break;
          default:
            badgeText = 'Unbekannt';
            badgeColor = 'bg-red-200 dark:bg-red-700';
            break;
        }

        return (
          <Badge variant="outline" className={cn(badgeColor, 'font-normal tracking-wider')}>
            {badgeText}
          </Badge>
        );
      },
    },

    {
      id: 'actions',
      header: 'Verwaltung',
      enableHiding: false,
      cell: UsermanagmentDropdown,
    },
  ];

  //eslint-disable-next-line
  const data: User[] = useMemo(() => generateUserList(), [userGroupsMapping]);

  return (
    <div className="flex flex-col h-[90svh]">
      <Box className="flex flex-col h-full  py-2 px-1 overflow-hidden">
        <div>
          <CreateUserForm teamName={teamName} isGlobalAdmin={isGlobalAdmin} isGroupAdmin={isGroupAdmin} />
        </div>

        <DataTable columns={columns} data={data} loading={loading} />
      </Box>
      <Footer />
    </div>
  );
};

export default GroupManagerWindow;
